import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SsoService } from '../services/sso.service';

/**
 * Native SSO step-up interceptor (#80, consuming #78).
 *
 * When an authenticated API call is refused with `403 sso_step_up_required` — the
 * session hasn't asserted the enforced district it's trying to reach — the server
 * returns a `step_up_url` (see SsoEnforcementApiFilter). This opens that URL in the
 * system browser with the PKCE hand-off appended, so the completed step-up returns a
 * code the app exchanges for a token carrying the newly asserted district.
 *
 * The original error still propagates so the calling component can show its own
 * state; step-up runs alongside. A simple in-flight guard avoids launching multiple
 * browser tabs when several calls 403 at once. SsoService is resolved lazily (it
 * depends on HttpClient) so this interceptor doesn't create a cyclic dependency.
 */
@Injectable()
export class SsoStepUpInterceptor implements HttpInterceptor {
  private inProgress = false;

  constructor(private injector: Injector) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse
            && err.status === 403
            && err.error && err.error.error === 'sso_step_up_required'
            && err.error.step_up_url
            && !this.inProgress) {
          this.inProgress = true;
          const sso = this.injector.get(SsoService);
          Promise.resolve(sso.startStepUp(err.error.step_up_url))
            .catch(() => { /* opening the browser failed — the propagated error still surfaces */ })
            .then(() => { this.inProgress = false; });
        }
        return throwError(err);
      }),
    );
  }
}
